import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';

export interface TreeElement {
    name: string;
    id: string;
    type: string;
    avatar?: string;
    icon?: string;
    children?: TreeElement[];
}

export type TreeSelectedEvent = Pick<TreeElement, 'id' | 'type'>;
export type TreeSelectedElement = Pick<TreeElement, 'id' | 'type'>;

@Component({

    selector: 'syn-tree-view',
    styleUrls: ['./tree-view.component.scss'],
    templateUrl: 'tree-view.component.html'
})
export class SynappsTreeViewComponent implements OnInit {

    @Input()
    public tree: Observable<TreeElement[]>;

    @Output()
    public selected = new EventEmitter<TreeSelectedEvent>();

    public items$: Observable<TreeElement[]>;
    public treeControl = new NestedTreeControl<TreeElement>(node => node.children);
    public currentSelectedElement: TreeSelectedElement | null = null;
    public currentSearchFilter: string | null = null;

    private searchValueSubject = new Subject<string>();



    @Input()
    public set selectedItem(value: TreeSelectedEvent | null) {
        this.currentSelectedElement = value ? { ...value } : null;
    }

    ngOnInit(): void {
        this.items$ = this.searchOnTree(this.tree, this.searchValueSubject.asObservable());
        setTimeout(() => this.searchValueSubject.next(''));
    }

    public search(value: string) {
        this.searchValueSubject.next(value);
        this.currentSearchFilter = value;
    }

    public select(node: TreeElement): void {
        this.currentSelectedElement = { id: node.id, type: node.type };
        this.selected.emit(this.currentSelectedElement);
    }

    public isSelected(node: TreeElement): boolean {
        return this.currentSelectedElement?.id === node.id;
    }

    public hasSelectedChildren(node: TreeElement): boolean {
        return Boolean(node.children?.find(children => children.id === this.currentSelectedElement?.id));
    }

    public hasChild(_: number, node: TreeElement): Boolean {
        return Boolean(node.children) && node.children.length > 0;
    }

    private searchOnTree(tree: Observable<TreeElement[]>, searchTerm: Observable<string>): Observable<TreeElement[]> {
        return searchTerm.pipe(
            debounceTime(100),
            switchMap((filter?: string) => {
                const preparedFilter = filter?.toLocaleUpperCase() ?? '';
                if (!preparedFilter || preparedFilter.length < 2) {
                    return tree;
                }

                return tree.pipe(
                    map((organizationsTree) => {
                        return organizationsTree.filter(treeElm =>
                            `${treeElm.id}_${treeElm.name}`.toLocaleUpperCase().includes(preparedFilter) ||
                            treeElm.children.some(({ id, name }) => `${id}_${name}`.toLocaleUpperCase().includes(preparedFilter))
                        );
                    }),
                    tap((filteredTree) => {
                        this.treeControl.dataNodes = filteredTree;
                        this.treeControl.expandAll();
                    })
                );
            }),
        );
    }
}
